/**
 * URL Utilities for handling various URL formats across the application
 * Centralizes all URL handling to maintain DRY principles
 *
 * This file serves as the single source of truth for URL manipulation,
 * environment variables, and general image URL processing.
 */

// ========================================================================
// URL & Path Normalization Functions
// ========================================================================

/**
 * Ensures an API endpoint path always starts with a forward slash
 * This is critical for proper URL construction when combined with a base URL
 *
 * @param path - The API endpoint path to normalize
 * @returns Normalized path with exactly one leading slash
 */
export const normalizeApiPath = (path: string): string => {
  if (!path) return "/";

  // Remove any 'api' prefix that might be duplicated
  let cleanPath = path.trim();

  // Replace 'apires/' with 'res/' to fix common pattern error
  cleanPath = cleanPath.replace(/^apires\//, "res/");
  cleanPath = cleanPath.replace(/\/apires\//, "/res/");

  // Ensure path starts with exactly one slash
  cleanPath = cleanPath.replace(/^\/*/, "/");

  return cleanPath;
};

/**
 * Ensures a URL ends with exactly one forward slash
 * @param url - The URL to normalize
 * @returns Normalized URL with exactly one trailing slash
 */
export const normalizeUrl = (url: string): string => {
  if (!url) return "";
  return url.trim().replace(/\/?$/, "/");
};

/**
 * Ensures a URL does NOT end with a forward slash
 * @param url - The URL to normalize
 * @returns Normalized URL without trailing slash
 */
export const normalizeUrlNoSlash = (url: string): string => {
  if (!url) return "";
  return url.trim().replace(/\/+$/, "");
};

// ========================================================================
// Environment URL Getters
// ========================================================================

/**
 * Gets the base API URL
 * @returns API URL directly from environment variable without modifications
 */
export const getApiBaseUrl = (): string => {
  // Return the NEXT_PUBLIC_API_URL directly without any modifications
  return process.env.NEXT_PUBLIC_API_URL || "";
};

/**
 * Gets the base images URL with proper forward slash
 * @returns Normalized images bucket URL with fallback
 */
export const getImagesBaseUrl = (): string => {
  const imagesBucket = process.env.NEXT_PUBLIC_IMAGES_BUCKET || "";

  if (
    !imagesBucket.trim() &&
    (process.env.NODE_ENV === "production" || typeof window === "undefined")
  ) {
    // Use a fallback URL for images in production or server-side
    return normalizeUrl("https://images.aventure.vc/bucket");
  }

  return normalizeUrl(imagesBucket);
};

/**
 * Gets the base general images URL with proper forward slash
 * @returns Normalized images URL
 */
export const getGeneralImagesBaseUrl = (): string => {
  return normalizeUrl(
    process.env.NEXT_PUBLIC_IMAGES_URL || "https://images.aventure.vc"
  );
};

/**
 * Gets the application base URL without trailing slash
 * Used for generating internal links
 * @returns Normalized application URL with fallback
 */
export const getAppBaseUrl = (): string => {
  const appUrl = process.env.NEXT_PUBLIC_APP_URL || "";

  if (
    !appUrl.trim() &&
    (process.env.NODE_ENV === "production" || typeof window === "undefined")
  ) {
    // Use a fallback URL for app in production or server-side
    return "https://aventure.vc";
  }

  return normalizeUrlNoSlash(appUrl);
};

// ========================================================================
// Default Images Config
// ========================================================================

/**
 * Default image paths used throughout the application
 */
export const defaultImages = {
  /** Default image for person/avatar */
  person: "/images/no-avatar.svg",
  /** Default image for company/organization */
  company: "/images/no-company.svg",
  /** Default image for generic content */
  content: "/images/default.png",
  /** Maps content types to their default images for easy lookups */
  getByType: (type: string): string => {
    switch (type.toLowerCase()) {
      case "person":
        return "/images/no-avatar.svg";
      case "company":
        return "/images/no-company.svg";
      default:
        return "/images/default.png";
    }
  },
};

// ========================================================================
// General URL Construction
// ========================================================================

/**
 * Constructs a full image URL from a relative path using general images URL
 *
 * @param imagePath - Relative path to the image
 * @param defaultImage - Optional default image path to use as fallback
 * @returns Full image URL or default if path is empty
 *
 * @see {@link src/utils/imgSelector.ts} - Utility that uses this function
 * @see {@link src/components/ui/DefaultImage.tsx} - Component that uses image URLs
 */
export const getFullImageUrl = (
  imagePath?: string,
  defaultImage: string = ""
): string => {
  // Return the default image if no path is provided
  if (!imagePath) return defaultImage;

  // If it's already an absolute URL, return it as is
  if (imagePath.startsWith("http")) return imagePath;

  // Get the base URL using the centralized function
  const baseUrl = getGeneralImagesBaseUrl();

  // Normalize the path using the API path normalization function
  const normalizedPath = normalizeApiPath(imagePath);

  // Remove trailing slash from base URL if it exists
  const cleanBaseUrl = normalizeUrlNoSlash(baseUrl);

  // Build the complete URL
  return `${cleanBaseUrl}${normalizedPath}`;
};

/**
 * Constructs a full internal link URL from a relative path
 * @param path - Relative path/slug
 * @returns Full application URL
 */
export const getInternalUrl = (path?: string): string => {
  if (!path) return getAppBaseUrl();
  // Remove leading slash from path if it exists
  const cleanPath = path.replace(/^\/+/, "");
  return `${getAppBaseUrl()}/${cleanPath}`;
};
